import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "hidden lg:block"
}
const _hoisted_2 = {
  key: 0,
  class: "w-80 sm:w-96"
}
const _hoisted_3 = { class: "text-lg mb-3 px-3 pt-3" }
const _hoisted_4 = { class: "px-3" }
const _hoisted_5 = { class: "w-full px-3 border-b dark:border-gray-900 bg-gray-50 dark:bg-gray-800" }
const _hoisted_6 = { class: "font-medium" }
const _hoisted_7 = { class: "font-sm text-gray-400" }
const _hoisted_8 = { class: "font-medium" }
const _hoisted_9 = { class: "font-sm text-gray-400" }
const _hoisted_10 = { class: "text-sm" }
const _hoisted_11 = {
  key: 0,
  class: "mb-4 px-3"
}
const _hoisted_12 = { class: "font-semibold mb-1" }
const _hoisted_13 = { class: "pt-3 border-t p-3 border-gray-200 dark:border-gray-700" }
const _hoisted_14 = /*#__PURE__*/_createTextVNode(" View this week's")
const _hoisted_15 = /*#__PURE__*/_createTextVNode(" liquidity mining incentives ")

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getAddress } from 'ethers/lib/utils';
import { differenceInSeconds } from 'date-fns';
import { useIntervalFn } from '@vueuse/core';

import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import useUserClaimsQuery from '@/composables/queries/useUserClaimsQuery';
import useBreakpoints from '@/composables/useBreakpoints';
import useEthers from '@/composables/useEthers';
import useTransactions from '@/composables/useTransactions';
import useTokens from '@/composables/useTokens';
import { networkId } from '@/composables/useNetwork';
import { oneSecondInMs } from '@/composables/useTime';

import { bnum } from '@/lib/utils';

import { claimService } from '@/services/claim/claim.service';
import useWeb3 from '@/services/web3/useWeb3';

import { TOKENS } from '@/constants/tokens';
import useTranasactionErrors, {
  TransactionError
} from '@/composables/useTransactionErrors';

type ClaimableToken = {
  token: string;
  symbol: string;
  amount: string;
  fiatValue: string;
};

enum Tabs {
  CLAIMABLE = 'claimable',
  CURRENT_ESTIMATE = 'currentEstimate'
}


export default _defineComponent({
  setup(__props) {

const { t } = useI18n();

const tabs = [
  { value: Tabs.CLAIMABLE, label: t('liquidityMiningPopover.tabs.claimable') },
  {
    value: Tabs.CURRENT_ESTIMATE,
    label: t('liquidityMiningPopover.tabs.currentEstimate')
  }
];

const activeTab = ref(tabs[0].value);
const isClaiming = ref(false);
const elapstedTimeSinceEstimateTimestamp = ref(0);
const claimError = ref<TransactionError | null>(null);

// COMPOSABLES
const { upToLargeBreakpoint } = useBreakpoints();
const userClaimsQuery = useUserClaimsQuery();
const { fNum2 } = useNumbers();
const { account, getProvider, isMismatchedNetwork } = useWeb3();
const { txListener } = useEthers();
const { addTransaction } = useTransactions();
const {
  priceFor,
  tokens,
  priceQueryLoading,
  loading: tokensLoading
} = useTokens();
const { parseError } = useTranasactionErrors();

const BALTokenAddress = getAddress(TOKENS.AddressMap[networkId.value].BAL);

// COMPUTED
const BALTokenPlaceholder = computed<ClaimableToken>(() => ({
  token: BALTokenAddress,
  symbol: tokens.value[BALTokenAddress]?.symbol,
  amount: '0',
  fiatValue: '0'
}));

const userClaims = computed(() =>
  userClaimsQuery.isSuccess.value ? userClaimsQuery.data?.value : null
);

const userClaimsLoading = computed(
  () =>
    userClaimsQuery.isLoading.value ||
    userClaimsQuery.isIdle.value ||
    priceQueryLoading.value ||
    tokensLoading.value
);

const claimableTokens = computed<ClaimableToken[]>(() => {
  if (
    userClaims.value != null &&
    userClaims.value.multiTokenPendingClaims.length > 0
  ) {
    return userClaims.value.multiTokenPendingClaims.map(
      ({ availableToClaim, tokenClaimInfo }) => ({
        token: tokenClaimInfo.token,
        symbol: tokens.value[tokenClaimInfo.token]?.symbol,
        amount: availableToClaim,
        fiatValue: bnum(availableToClaim)
          .times(priceFor(tokenClaimInfo.token))
          .toString()
      })
    );
  }
  return [BALTokenPlaceholder.value];
});

const currentEstimateClaimableTokens = computed<ClaimableToken[]>(() => {
  if (
    userClaims.value != null &&
    userClaims.value.multiTokenCurrentRewardsEstimate.length > 0
  ) {
    return userClaims.value.multiTokenCurrentRewardsEstimate.map(
      ({ token, rewards, velocity }) => {
        const rewardsSinceTimestamp = bnum(velocity).times(
          elapstedTimeSinceEstimateTimestamp.value
        );
        const totalRewards = bnum(rewards).plus(rewardsSinceTimestamp);

        return {
          token,
          symbol: tokens.value[token]?.symbol,
          amount: totalRewards.toString(),
          fiatValue: totalRewards.times(priceFor(token)).toString()
        };
      }
    );
  }
  return [BALTokenPlaceholder.value];
});

const totalClaimableTokensFiatValue = computed(() =>
  claimableTokens.value
    .reduce((totalValue, { fiatValue }) => totalValue.plus(fiatValue), bnum(0))
    .toString()
);

const totalCurrentEstimateClaimableTokensFiatValue = computed(() =>
  currentEstimateClaimableTokens.value
    .reduce((totalValue, { fiatValue }) => totalValue.plus(fiatValue), bnum(0))
    .toString()
);

const totalRewardsFiatValue = computed(() =>
  bnum(totalClaimableTokensFiatValue.value)
    .plus(totalCurrentEstimateClaimableTokensFiatValue.value)
    .toString()
);

const hasClaimableTokens = computed(() =>
  claimableTokens.value.some(
    claimableToken => Number(claimableToken.amount) > 0
  )
);

const hasCurrentEstimateClaimableTokens = computed(() =>
  currentEstimateClaimableTokens.value.some(
    claimableToken => Number(claimableToken.amount) > 0
  )
);

useIntervalFn(async () => {
  if (userClaims.value != null && userClaims.value.timestamp != null) {
    const diffInSeconds = differenceInSeconds(
      new Date(),
      new Date(userClaims.value.timestamp)
    );
    elapstedTimeSinceEstimateTimestamp.value = diffInSeconds;
  }
}, oneSecondInMs);

watch(isMismatchedNetwork, () => {
  userClaimsQuery.refetch.value();
});

// METHODS
async function claimAvailableRewards() {
  if (userClaims.value != null) {
    isClaiming.value = true;
    claimError.value = null;

    try {
      const tx = await claimService.multiTokenClaimRewards(
        getProvider(),
        account.value,
        userClaims.value.multiTokenPendingClaims
      );

      const summary = claimableTokens.value
        .map(
          claimableToken =>
            `${fNum2(claimableToken.amount, {
              minimumFractionDigits: 4,
              maximumFractionDigits: 4
            })} ${claimableToken.symbol}`
        )
        .join(', ');

      addTransaction({
        id: tx.hash,
        type: 'tx',
        action: 'claim',
        summary
      });

      txListener(tx, {
        onTxConfirmed: () => {
          isClaiming.value = false;
          userClaimsQuery.refetch.value();
        },
        onTxFailed: () => {
          isClaiming.value = false;
        }
      });
    } catch (e) {
      console.log(e);
      claimError.value = parseError(e);
      isClaiming.value = false;
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_StarsIcon = _resolveComponent("StarsIcon")!
  const _component_BalLoadingIcon = _resolveComponent("BalLoadingIcon")!
  const _component_BalBtn = _resolveComponent("BalBtn")!
  const _component_BalTabs = _resolveComponent("BalTabs")!
  const _component_BalAsset = _resolveComponent("BalAsset")!
  const _component_BalCard = _resolveComponent("BalCard")!
  const _component_BalAlert = _resolveComponent("BalAlert")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_BalPopover = _resolveComponent("BalPopover")!

  return (_openBlock(), _createBlock(_component_BalPopover, { "no-pad": "" }, {
    activator: _withCtx(() => [
      _createVNode(_component_BalBtn, {
        color: "white",
        class: "mr-2 text-base",
        size: _unref(upToLargeBreakpoint) ? 'md' : 'sm',
        circle: _unref(upToLargeBreakpoint)
      }, {
        default: _withCtx(() => [
          (_unref(upToLargeBreakpoint) ? !_unref(userClaimsLoading) : true)
            ? (_openBlock(), _createBlock(_component_StarsIcon, {
                key: 0,
                class: _normalizeClass({ 'mr-2': !_unref(upToLargeBreakpoint) })
              }, null, 8, ["class"]))
            : _createCommentVNode("", true),
          (_unref(userClaimsLoading))
            ? (_openBlock(), _createBlock(_component_BalLoadingIcon, {
                key: 1,
                size: "sm"
              }))
            : (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(_unref(fNum2)(_unref(totalRewardsFiatValue), _unref(FNumFormats).fiat)), 1))
        ]),
        _: 1
      }, 8, ["size", "circle"])
    ]),
    default: _withCtx(() => [
      (_unref(userClaims) != null)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("h5", _hoisted_3, _toDisplayString(_ctx.$t('liquidityMiningPopover.title')), 1),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_BalCard, {
                "no-pad": "",
                class: "mb-4"
              }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_component_BalTabs, {
                      modelValue: activeTab.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = $event)),
                      tabs: tabs,
                      class: "whitespace-nowrap p-0 m-0",
                      "no-pad": ""
                    }, null, 8, ["modelValue"])
                  ])
                ]),
                default: _withCtx(() => [
                  (activeTab.value === Tabs.CLAIMABLE)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(claimableTokens), (claimableToken) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `token-${claimableToken.token}`,
                          class: "px-3 py-2 flex items-center mb-2 border-b dark:border-gray-900 last:border-0"
                        }, [
                          _createVNode(_component_BalAsset, {
                            address: claimableToken.token,
                            size: 36,
                            class: "mr-3"
                          }, null, 8, ["address"]),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(fNum2)(claimableToken.amount, _unref(FNumFormats).token)) + " " + _toDisplayString(claimableToken.symbol), 1),
                            _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(fNum2)(claimableToken.fiatValue, _unref(FNumFormats).fiat)), 1)
                          ])
                        ]))
                      }), 128))
                    : _createCommentVNode("", true),
                  (activeTab.value === Tabs.CURRENT_ESTIMATE)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(currentEstimateClaimableTokens), (claimableToken) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `token-${claimableToken.token}`,
                          class: "px-3 py-2 flex items-center mb-2 border-b dark:border-gray-900 last:border-0"
                        }, [
                          _createVNode(_component_BalAsset, {
                            address: claimableToken.token,
                            size: 36,
                            class: "mr-3"
                          }, null, 8, ["address"]),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(fNum2)(claimableToken.amount, _unref(FNumFormats).token)) + " " + _toDisplayString(claimableToken.symbol), 1),
                            _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(fNum2)(claimableToken.fiatValue, _unref(FNumFormats).fiat)), 1)
                          ])
                        ]))
                      }), 128))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(_component_BalBtn, {
                color: "gradient",
                size: "md",
                block: "",
                class: "mb-6",
                loading: isClaiming.value,
                "loading-label": _ctx.$t('claiming'),
                onClick: claimAvailableRewards,
                disabled: !_unref(hasClaimableTokens)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('claimAll')) + " ", 1),
                  (_unref(hasClaimableTokens))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode("~" + _toDisplayString(_unref(fNum2)(_unref(totalClaimableTokensFiatValue), _unref(FNumFormats).fiat)), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["loading", "loading-label", "disabled"]),
              (claimError.value != null)
                ? (_openBlock(), _createBlock(_component_BalAlert, {
                    key: 0,
                    class: "mb-6 -mt-4",
                    type: "error",
                    size: "md",
                    title: claimError.value.title,
                    description: claimError.value.description,
                    block: "",
                    "action-label": "Dismiss",
                    onActionClick: _cache[1] || (_cache[1] = ($event: any) => (claimError.value = null))
                  }, null, 8, ["title", "description"]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_10, [
              (!_unref(hasClaimableTokens) && !_unref(hasCurrentEstimateClaimableTokens))
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t('liquidityMiningPopover.noRewards.title')), 1),
                    _createElementVNode("div", null, _toDisplayString(_ctx.$t('liquidityMiningPopover.noRewards.description')), 1)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                _hoisted_14,
                _createVNode(_component_router_link, {
                  to: { name: 'liquidity-mining' },
                  class: "text-red-500 hover:underline"
                }, {
                  default: _withCtx(() => [
                    _hoisted_15
                  ]),
                  _: 1
                })
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})